import type { Totals } from "../../lib/exam/scoring-types";

export type PassFailHeroProps = {
  totals: Totals;
  /** "Goed gedaan, {naam}!" headline name. Omit name if empty. */
  praise: string;
  /** Band-coloured status pill text ("Geslaagd" / "Niet gehaald"). */
  statusLabel: string;
  /** Optional serif-italic hero summary line (first line of the LLM summary). */
  summaryLine?: string;
};

/**
 * Hero verdict — typographic, centered. The personalized praise headline
 * IS the verdict (Fredoka via the h1 element rule); a band-coloured pill
 * carries the pass/fail status and a serif-italic line carries the score.
 * Deliberately NOT a 140px score ring (brand.md §7 bans the decorative
 * metric-ring hero; the type carries the weight).
 */
export function PassFailHero({ totals, praise, statusLabel, summaryLine }: PassFailHeroProps) {
  const pct = Math.round(totals.percentage * 100);
  const passed = totals.passed;
  const pillClass = passed
    ? "bg-[var(--pass)]/15 text-[var(--pass)]"
    : "bg-[var(--fail)]/15 text-[var(--fail)]";

  return (
    <section className="text-center space-y-4" aria-label="Eindresultaat">
      <span
        className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 font-mono-badge text-xs font-semibold uppercase tracking-wider ${pillClass}`}
      >
        {passed ? (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden>
            <path d="M5 12l5 5L20 6" />
          </svg>
        ) : null}
        {statusLabel}
      </span>

      <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{praise}</h1>

      <p className="font-mono-badge text-sm text-[var(--ink-muted)] tabular-nums">
        {pct}% — {totals.total} van {totals.max} punten
      </p>

      {summaryLine ? (
        <p className="font-serif-italic text-lg sm:text-xl text-[var(--ink-muted)] max-w-prose mx-auto leading-relaxed">
          {summaryLine}
        </p>
      ) : null}
    </section>
  );
}
